import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Add,
  ChatBubbleOutline,
  Close,
  DeleteOutline,
  Email,
  Edit,
  Facebook,
  Instagram,
  Language,
  PhoneIphone,
  Refresh,
  Save,
  Security,
  Settings,
  WhatsApp,
} from "@material-ui/icons";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Hidden,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import QRCode from "qrcode.react";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const publicMetaCallback = `${window.location.origin}/crm/meta/callback`;
const initialState = {
  appId: "",
  apiVersion: "v22.0",
  redirectUri: publicMetaCallback,
  facebookMessenger: true,
  instagramDirect: true,
  facebookLeads: false,
  status: "sin_configurar",
};
const primaryWhatsappConnection = { instanceName: "crm-pruebas", inboxName: "WhatsApp pruebas", label: "WhatsApp pruebas" };
const chatwootBaseUrl = "https://chatwoot.srv1820832.hstgr.cloud/app/accounts/2";
const chatwootNewInboxUrl = `${chatwootBaseUrl}/settings/inboxes/new`;

const useStyles = makeStyles(theme => ({
  scroll: { overflowY: "auto", padding: theme.spacing(2) },
  intro: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  table: { minWidth: 760 },
  channelIdentity: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), minWidth: 0 },
  channelIcon: {
    width: 42,
    height: 42,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.action.hover,
    flex: "0 0 auto",
  },
  mobileCard: { marginBottom: theme.spacing(1.5) },
  mobileLine: { display: "flex", justifyContent: "space-between", gap: theme.spacing(2), marginTop: theme.spacing(1) },
  dialogTitle: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: theme.spacing(1) },
  optionCard: {
    height: "100%",
    cursor: "pointer",
    transition: "transform .15s ease, box-shadow .15s ease",
    "&:hover": { transform: "translateY(-2px)", boxShadow: theme.shadows[4] },
  },
  optionIcon: {
    width: 46,
    height: 46,
    borderRadius: 13,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.action.hover,
    marginBottom: theme.spacing(1.5),
  },
  qrArea: { display: "flex", alignItems: "center", gap: theme.spacing(2.5), flexWrap: "wrap" },
  qrFrame: {
    width: 238,
    height: 238,
    padding: theme.spacing(1.25),
    borderRadius: 14,
    background: "#fff",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  qrImage: { width: 216, height: 216, objectFit: "contain", display: "block" },
  qrPlaceholder: {
    width: 238,
    height: 238,
    borderRadius: 14,
    border: `1px dashed ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    textAlign: "center",
    padding: theme.spacing(2),
  },
  qrInfo: { flex: "1 1 280px", minWidth: 0 },
  actions: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap", marginTop: theme.spacing(2) },
  errorBox: { marginTop: theme.spacing(1.5), padding: theme.spacing(1.25, 1.5), borderRadius: 8, color: theme.palette.error.dark, background: theme.palette.error.light },
  detailGrid: { display: "grid", gridTemplateColumns: "minmax(120px, 170px) 1fr", gap: theme.spacing(1.25), marginTop: theme.spacing(2) },
}));

const connectionLabel = state => {
  const normalized = String(state || "").toLowerCase();
  if (normalized.includes("open")) return "Conectado";
  if (normalized.includes("connect")) return "Esperando QR";
  if (normalized.includes("close")) return "Desconectado";
  if (normalized.includes("no_creada")) return "Sin instancia";
  if (normalized.includes("consult")) return "Comprobando";
  return "Pendiente";
};

const isConnectedState = state => String(state || "").toLowerCase().includes("open");

const qrPresentation = value => {
  if (!value || typeof value !== "string") return null;
  if (/^(data:image\/|https?:\/\/|blob:)/i.test(value)) return { kind: "image", value };
  if (/^[A-Za-z0-9+/\r\n]+={0,2}$/.test(value) && value.length > 500) {
    return { kind: "image", value: `data:image/png;base64,${value.replace(/\s/g, "")}` };
  }
  return { kind: "code", value };
};

const TikTokMark = ({ size = 30 }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: size, height: size }}>
    <path fill="#25F4EE" d="M15.6 3.2c.4 2.2 1.7 3.7 3.8 4.2v3.2c-1.5-.1-2.8-.6-3.8-1.4v6.1c0 3.7-2.5 5.7-5.6 5.7-2.8 0-5.1-1.9-5.1-4.8 0-3 2.4-5 5.5-5.1v3.2c-1.3 0-2.2.7-2.2 1.8 0 1 .8 1.6 1.8 1.6 1.1 0 2.2-.7 2.2-2.6V3.2h3.4Z" />
    <path fill="#FE2C55" d="M17 2c.4 2.2 1.7 3.7 3.8 4.2v2.2c-1.5-.1-2.8-.6-3.8-1.4v6.1c0 3.7-2.5 5.7-5.6 5.7-.8 0-1.5-.2-2.1-.5.3.1.7.1 1 .1 1.1 0 2.2-.7 2.2-2.6V2H17Z" />
    <path fill="#111827" d="M15.6 2v11.3c0 1.9-1.1 2.6-2.2 2.6-1 0-1.8-.6-1.8-1.6 0-1.1.9-1.8 2.2-1.8V9.3c-3.1.1-5.5 2.1-5.5 5.1 0 2.9 2.3 4.8 5.1 4.8 3.1 0 5.6-2 5.6-5.7V7.8c1 .8 2.3 1.3 3.8 1.4V7.4C20.1 6.9 18.8 5.4 18.4 3.2V2h-2.8Z" />
  </svg>
);

const typeLabel = channel => {
  const type = String(channel.type || channel.channelType || "").toLowerCase();
  if (type.includes("facebook")) return "Facebook Messenger";
  if (type.includes("instagram")) return "Instagram profesional";
  if (type.includes("tiktok") || type.includes("tik tok")) return "TikTok Business";
  if (type.includes("whatsapp")) return "WhatsApp";
  if (type.includes("email")) return "Correo electrónico";
  if (type.includes("web")) return "Chat para sitio web";
  return channel.label || channel.channelType || "Canal de Chatwoot";
};

const MetaIntegration = () => {
  const classes = useStyles();
  const autoRenewing = useRef(false);
  const [config, setConfig] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [qrStatus, setQrStatus] = useState({ state: "connecting", qr: null, instanceName: primaryWhatsappConnection.instanceName });
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState("");
  const [qrExpiresAt, setQrExpiresAt] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [connections, setConnections] = useState([primaryWhatsappConnection]);
  const [activeInstance, setActiveInstance] = useState(primaryWhatsappConnection.instanceName);
  const [connectionName, setConnectionName] = useState("");
  const [creatingConnection, setCreatingConnection] = useState(false);
  const [deletingConnection, setDeletingConnection] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [renamingConnection, setRenamingConnection] = useState(false);
  const [platformChannels, setPlatformChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [newWhatsappOpen, setNewWhatsappOpen] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [metaDialogOpen, setMetaDialogOpen] = useState(false);
  const [tiktokDialogOpen, setTiktokDialogOpen] = useState(false);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);

  const normalizeMetaConfig = useCallback(data => {
    const merged = { ...initialState, ...(data || {}) };
    if (window.location.hostname !== "localhost" && /127\.0\.0\.1|localhost/i.test(merged.redirectUri || "")) {
      merged.redirectUri = publicMetaCallback;
    }
    return merged;
  }, []);

  const loadPlatformChannels = useCallback(async () => {
    setChannelsLoading(true);
    try {
      const { data } = await api.get("/crm/channels", { params: { _ts: Date.now() } });
      setPlatformChannels(Array.isArray(data.channels) ? data.channels : []);
    } catch (error) {
      setPlatformChannels([]);
      throw error;
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  const loadQrForInstance = useCallback(async (instanceName, showError = false) => {
    if (!instanceName) return;
    try {
      const { data } = await api.get(`/crm/whatsapp-connections/${encodeURIComponent(instanceName)}/status`, { params: { _ts: Date.now() } });
      setQrStatus(data);
      setQrExpiresAt(Number(data.qrExpiresAt || 0));
      setQrError("");
      setConnections(current => current.map(connection => connection.instanceName === instanceName ? { ...connection, state: data.state } : connection));
    } catch (error) {
      const message = error?.response?.data?.error || error?.response?.data?.message || "No se pudo consultar Evolution API.";
      setQrError(message);
      if (showError) toastError(error);
    }
  }, []);

  const loadConnections = useCallback(async preferredInstance => {
    const { data } = await api.get("/crm/whatsapp-connections", { params: { _ts: Date.now() } });
    const items = Array.isArray(data.connections) ? data.connections : [];
    setConnections(items);
    const selected = items.find(item => item.instanceName === preferredInstance)
      || items.find(item => item.instanceName === activeInstance)
      || items.find(item => item.instanceName === primaryWhatsappConnection.instanceName)
      || items[0];
    if (selected) {
      setActiveInstance(selected.instanceName);
      await loadQrForInstance(selected.instanceName, false);
    }
    return items;
  }, [activeInstance, loadQrForInstance]);

  const refreshAll = useCallback(async (showToast = false) => {
    try {
      await Promise.all([loadPlatformChannels(), loadConnections(activeInstance)]);
      if (showToast) toast.success("Canales sincronizados.");
    } catch (error) {
      toastError(error);
    }
  }, [activeInstance, loadConnections, loadPlatformChannels]);

  useEffect(() => {
    api.get("/crm/meta").then(({ data }) => setConfig(normalizeMetaConfig(data))).catch(toastError);
    refreshAll(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connected = isConnectedState(qrStatus.state);
  const qr = useMemo(() => qrPresentation(qrStatus.qr), [qrStatus.qr]);
  const activeConnection = connections.find(connection => connection.instanceName === activeInstance);

  useEffect(() => {
    if (!whatsappDialogOpen || !activeInstance) return undefined;
    const interval = window.setInterval(() => loadQrForInstance(activeInstance, false), 10000);
    return () => window.clearInterval(interval);
  }, [activeInstance, loadQrForInstance, whatsappDialogOpen]);

  useEffect(() => {
    if (!qr || connected || !qrExpiresAt) {
      setSecondsLeft(0);
      return undefined;
    }
    const updateCountdown = () => {
      const remaining = Math.max(0, Math.ceil((qrExpiresAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0 && !autoRenewing.current) {
        autoRenewing.current = true;
        loadQrForInstance(activeInstance, false).finally(() => { autoRenewing.current = false; });
      }
    };
    updateCountdown();
    const interval = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(interval);
  }, [activeInstance, connected, loadQrForInstance, qr, qrExpiresAt]);

  const channelRows = useMemo(() => {
    const whatsappRows = connections.map(connection => ({
      rowKey: `evolution:${connection.instanceName}`,
      kind: "whatsappQr",
      id: connection.inboxId || "—",
      name: connection.label || connection.inboxName || connection.profileName || connection.instanceName,
      type: "WhatsApp QR",
      team: "Chatwoot",
      state: connection.state,
      connected: isConnectedState(connection.state),
      raw: connection,
    }));
    const whatsappInboxIds = new Set(connections.map(connection => Number(connection.inboxId)).filter(Boolean));
    const whatsappInstances = new Set(connections.map(connection => connection.instanceName).filter(Boolean));
    const otherRows = platformChannels
      .filter(channel => !whatsappInboxIds.has(Number(channel.id)) && !whatsappInstances.has(channel.instanceName))
      .map(channel => ({
        rowKey: `chatwoot:${channel.id}`,
        kind: "platform",
        id: channel.id,
        name: channel.name || channel.label,
        type: typeLabel(channel),
        team: "Chatwoot",
        state: channel.connected ? "open" : "closed",
        connected: Boolean(channel.connected),
        raw: channel,
      }));
    return [...whatsappRows, ...otherRows];
  }, [connections, platformChannels]);

  const connectedCount = channelRows.filter(channel => channel.connected).length;

  const iconFor = row => {
    const type = String(row.type || "").toLowerCase();
    if (type.includes("whatsapp")) return <WhatsApp style={{ color: "#22a559" }} />;
    if (type.includes("facebook")) return <Facebook color="primary" />;
    if (type.includes("instagram")) return <Instagram style={{ color: "#e94b6a" }} />;
    if (type.includes("tiktok") || type.includes("tik tok")) return <TikTokMark size={24} />;
    if (type.includes("correo")) return <Email color="action" />;
    if (type.includes("web")) return <Language color="primary" />;
    return <ChatBubbleOutline color="action" />;
  };

  const updateConfig = (key, value) => setConfig(current => ({ ...current, [key]: value }));

  const saveMeta = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/crm/meta", config);
      setConfig(normalizeMetaConfig(data));
      toast.success("Configuración de Meta guardada.");
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const connectMeta = async () => {
    setSaving(true);
    try {
      const saved = await api.put("/crm/meta", config);
      setConfig(normalizeMetaConfig(saved.data));
      const { data } = await api.get("/crm/meta/authorize");
      window.open(data.authorizationUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const startWhatsApp = async () => {
    if (!activeInstance) return;
    setQrLoading(true);
    try {
      const { data } = await api.post(`/crm/whatsapp-connections/${encodeURIComponent(activeInstance)}/start`);
      setQrStatus(data);
      setQrError("");
      setQrExpiresAt(Number(data.qrExpiresAt || Date.now() + 45000));
      setConnections(current => current.map(connection => connection.instanceName === activeInstance ? { ...connection, state: data.state } : connection));
      toast.success("Código QR actualizado.");
    } catch (error) {
      toastError(error);
    } finally {
      setQrLoading(false);
    }
  };

  const relinkWithHistory = async () => {
    if (!activeInstance) return;
    const confirmed = window.confirm("Se cerrará únicamente la sesión actual de WhatsApp para generar un QR nuevo. La bandeja y sus conversaciones no se borrarán. ¿Continuar?");
    if (!confirmed) return;
    setQrLoading(true);
    try {
      const { data } = await api.post(`/crm/whatsapp-connections/${encodeURIComponent(activeInstance)}/relink-history`);
      setQrStatus(data);
      setQrError("");
      setQrExpiresAt(Number(data.qrExpiresAt || Date.now() + 45000));
      toast.info("Escaneá el QR nuevo para importar los chats disponibles.");
    } catch (error) {
      toastError(error);
    } finally {
      setQrLoading(false);
    }
  };

  const deleteWhatsappConnection = async () => {
    if (!activeInstance || !activeConnection) return;
    const label = activeConnection.label || activeConnection.inboxName || activeInstance;
    const confirmed = window.confirm(`¿Eliminar “${label}”?\n\nSe quitarán el dispositivo de Evolution API y su bandeja de Chatwoot. Las conversaciones de esa bandeja podrían dejar de estar disponibles desde el CRM. Esta acción no se puede deshacer.`);
    if (!confirmed) return;
    setDeletingConnection(true);
    try {
      const { data } = await api.delete(`/crm/whatsapp-connections/${encodeURIComponent(activeInstance)}`);
      setConnections(current => current.filter(connection => connection.instanceName !== activeInstance));
      if (data.inboxId) setPlatformChannels(current => current.filter(channel => Number(channel.id) !== Number(data.inboxId)));
      setWhatsappDialogOpen(false);
      setActiveInstance("");
      setQrStatus({ state: "no_creada", qr: null, instanceName: "" });
      if (Array.isArray(data.warnings) && data.warnings.length) toast.warn(data.warnings.join(" "));
      else toast.success(`Canal “${label}” eliminado.`);
      window.setTimeout(() => refreshAll(false), 2500);
    } catch (error) {
      toastError(error);
    } finally {
      setDeletingConnection(false);
    }
  };

  const openRenameConnection = () => {
    if (!activeConnection) return;
    setRenameName(activeConnection.label || activeConnection.inboxName || activeConnection.instanceName || "");
    setRenameDialogOpen(true);
  };

  const renameWhatsappConnection = async () => {
    const name = renameName.trim();
    if (!activeInstance || name.length < 2 || renamingConnection) return;
    setRenamingConnection(true);
    try {
      const { data } = await api.put(`/crm/whatsapp-connections/${encodeURIComponent(activeInstance)}`, { name });
      setConnections(current => current.map(connection => connection.instanceName === activeInstance ? { ...connection, ...data } : connection));
      setPlatformChannels(current => current.map(channel => Number(channel.id) === Number(data.inboxId) ? { ...channel, name } : channel));
      setRenameDialogOpen(false);
      toast.success("Nombre del canal actualizado.");
    } catch (error) {
      toastError(error);
    } finally {
      setRenamingConnection(false);
    }
  };

  const openWhatsappConfiguration = async connection => {
    setActiveInstance(connection.instanceName);
    setQrStatus({ state: connection.state || "consultando", qr: null, instanceName: connection.instanceName });
    setWhatsappDialogOpen(true);
    setQrLoading(true);
    try {
      await loadQrForInstance(connection.instanceName, true);
    } finally {
      setQrLoading(false);
    }
  };

  const configureChannel = row => {
    if (row.kind === "whatsappQr") {
      openWhatsappConfiguration(row.raw);
      return;
    }
    setSelectedChannel(row);
    setChannelDialogOpen(true);
  };

  const createWhatsappConnection = async () => {
    const name = connectionName.trim();
    if (name.length < 2) return toast.info("Escribí un nombre como Ventas, Reparto o Local.");
    setCreatingConnection(true);
    try {
      const { data } = await api.post("/crm/whatsapp-connections", { name });
      setConnectionName("");
      setNewWhatsappOpen(false);
      await loadConnections(data.instanceName);
      await loadPlatformChannels();
      setActiveInstance(data.instanceName);
      setQrStatus(data);
      setQrExpiresAt(Number(data.qrExpiresAt || Date.now() + 45000));
      setWhatsappDialogOpen(true);
      toast.success(`Canal “WhatsApp ${name}” creado. Escaneá el QR.`);
    } catch (error) {
      toastError(error);
    } finally {
      setCreatingConnection(false);
    }
  };

  const chooseChannel = option => {
    setConnectDialogOpen(false);
    if (option === "whatsappQr") setNewWhatsappOpen(true);
    if (option === "meta") setMetaDialogOpen(true);
    if (option === "tiktok") setTiktokDialogOpen(true);
    if (option === "whatsappOfficial" || option === "web" || option === "email") {
      window.open(chatwootNewInboxUrl, "_blank", "noopener,noreferrer");
    }
  };

  const channelOptions = [
    { key: "whatsappQr", title: "WhatsApp por QR", detail: "Evolution API. Ideal para vincular uno o varios números rápidamente.", icon: <WhatsApp style={{ color: "#22a559", fontSize: 30 }} /> },
    { key: "meta", title: "Facebook e Instagram", detail: "Conexión oficial de Meta para Messenger e Instagram profesional.", icon: <Facebook color="primary" style={{ fontSize: 30 }} /> },
    { key: "tiktok", title: "TikTok Business", detail: "Prepará una bandeja API para recibir los mensajes de tu cuenta comercial.", icon: <TikTokMark /> },
    { key: "whatsappOfficial", title: "WhatsApp Business oficial", detail: "Cloud API de Meta para otro número empresarial.", icon: <PhoneIphone color="primary" style={{ fontSize: 30 }} /> },
    { key: "web", title: "Chat para sitio web", detail: "Recibí consultas desde la web en una bandeja propia.", icon: <Language color="primary" style={{ fontSize: 30 }} /> },
    { key: "email", title: "Correo electrónico", detail: "Centralizá los correos del negocio junto a las conversaciones.", icon: <Email color="action" style={{ fontSize: 30 }} /> },
  ];

  const channelRow = row => (
    <TableRow key={row.rowKey} hover>
      <TableCell>
        <div className={classes.channelIdentity}>
          <div className={classes.channelIcon}>{iconFor(row)}</div>
          <div style={{ minWidth: 0 }}>
            <Typography style={{ fontWeight: 700 }} noWrap>{row.name}</Typography>
            <Typography variant="body2" color="textSecondary">{row.type}</Typography>
          </div>
        </div>
      </TableCell>
      <TableCell>{row.team}</TableCell>
      <TableCell><Chip size="small" color={row.connected ? "primary" : "default"} label={row.kind === "whatsappQr" ? connectionLabel(row.state) : (row.connected ? "Sincronizado" : "Pendiente")} /></TableCell>
      <TableCell>{row.id}</TableCell>
      <TableCell align="right">
        <Tooltip title={`Configurar ${row.name}`}>
          <Button size="small" startIcon={<Settings />} color="primary" onClick={() => configureChannel(row)}>Configurar</Button>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>Canales</Title>
        <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => setConnectDialogOpen(true)}>Conectar canal</Button>
      </MainHeader>
      <div className={classes.scroll}>
        <div className={classes.intro}>
          <div>
            <Typography variant="h6">{connectedCount} de {channelRows.length} conectados</Typography>
            <Typography color="textSecondary">Las líneas por donde te escriben tus contactos, organizadas en un solo lugar.</Typography>
          </div>
          <Button startIcon={channelsLoading ? <CircularProgress size={18} /> : <Refresh />} disabled={channelsLoading} onClick={() => refreshAll(true)}>
            {channelsLoading ? "Sincronizando…" : "Sincronizar canales"}
          </Button>
        </div>

        <Hidden smDown>
          <TableContainer component={Paper} variant="outlined">
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>CANAL</TableCell>
                  <TableCell>EQUIPO PREDETERMINADO</TableCell>
                  <TableCell>SINCRONIZACIÓN</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell align="right">ACCIONES</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {channelRows.map(channelRow)}
                {!channelRows.length && <TableRow><TableCell colSpan={5}><Typography color="textSecondary">Todavía no hay canales. Usá “Conectar canal” para comenzar.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Hidden>

        <Hidden mdUp>
          {channelRows.map(row => (
            <Card key={row.rowKey} variant="outlined" className={classes.mobileCard}>
              <CardContent>
                <div className={classes.channelIdentity}>
                  <div className={classes.channelIcon}>{iconFor(row)}</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Typography style={{ fontWeight: 700 }}>{row.name}</Typography>
                    <Typography variant="body2" color="textSecondary">{row.type}</Typography>
                  </div>
                  <Chip size="small" color={row.connected ? "primary" : "default"} label={row.kind === "whatsappQr" ? connectionLabel(row.state) : (row.connected ? "Sincronizado" : "Pendiente")} />
                </div>
                <div className={classes.mobileLine}><Typography variant="body2" color="textSecondary">Equipo</Typography><Typography variant="body2">{row.team}</Typography></div>
                <div className={classes.mobileLine}><Typography variant="body2" color="textSecondary">ID</Typography><Typography variant="body2">{row.id}</Typography></div>
                <Button fullWidth style={{ marginTop: 14 }} variant="outlined" color="primary" startIcon={<Settings />} onClick={() => configureChannel(row)}>Configurar canal</Button>
              </CardContent>
            </Card>
          ))}
        </Hidden>
      </div>

      <Dialog open={connectDialogOpen} onClose={() => setConnectDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle disableTypography>
          <div className={classes.dialogTitle}>
            <div><Typography variant="h6">Conectar canal</Typography><Typography variant="body2" color="textSecondary">Elegí de dónde llegarán las conversaciones.</Typography></div>
            <IconButton aria-label="cerrar" onClick={() => setConnectDialogOpen(false)}><Close /></IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {channelOptions.map(option => (
              <Grid item xs={12} sm={6} md={4} key={option.key}>
                <Card variant="outlined" className={classes.optionCard} role="button" tabIndex={0} onClick={() => chooseChannel(option.key)} onKeyDown={event => { if (event.key === "Enter") chooseChannel(option.key); }}>
                  <CardContent>
                    <div className={classes.optionIcon}>{option.icon}</div>
                    <Typography variant="h6">{option.title}</Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>{option.detail}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog open={newWhatsappOpen} onClose={() => !creatingConnection && setNewWhatsappOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Nuevo canal de WhatsApp QR</DialogTitle>
        <DialogContent>
          <Typography color="textSecondary" paragraph>Asignale un nombre claro. Cada número crea su propia bandeja en Chatwoot.</Typography>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            label="Nombre del canal"
            placeholder="Ej.: Local, Ventas o Reparto"
            value={connectionName}
            onChange={event => setConnectionName(event.target.value)}
            onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); createWhatsappConnection(); } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewWhatsappOpen(false)} disabled={creatingConnection}>Cancelar</Button>
          <Button variant="contained" color="primary" startIcon={creatingConnection ? <CircularProgress size={18} /> : <Add />} disabled={creatingConnection || connectionName.trim().length < 2} onClick={createWhatsappConnection}>Crear y mostrar QR</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={whatsappDialogOpen} onClose={() => setWhatsappDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle disableTypography>
          <div className={classes.dialogTitle}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Typography variant="h6">{activeConnection?.label || activeConnection?.inboxName || "WhatsApp QR"}</Typography>
                <Tooltip title="Modificar nombre" arrow><span><IconButton size="small" disabled={!activeConnection} onClick={openRenameConnection} aria-label="Modificar nombre del canal"><Edit fontSize="small" /></IconButton></span></Tooltip>
              </div>
              <Typography variant="body2" color="textSecondary">Evolution API · Bandeja {activeConnection?.inboxId || "pendiente"}</Typography>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Chip size="small" color={connected ? "primary" : "default"} label={connectionLabel(qrStatus.state)} />
              <IconButton aria-label="cerrar" onClick={() => setWhatsappDialogOpen(false)}><Close /></IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.qrArea}>
            {qr ? <div className={classes.qrFrame}>
              {qr.kind === "image" ? <img key={qrStatus.qrUpdatedAt || qr.value} className={classes.qrImage} src={qr.value} alt="Código QR de WhatsApp" /> : <QRCode key={qrStatus.qrUpdatedAt || qr.value} value={qr.value} size={216} level="M" />}
            </div> : <div className={classes.qrPlaceholder}>
              {qrLoading ? <CircularProgress size={34} /> : connected ? <WhatsApp style={{ color: "#22a559", fontSize: 48 }} /> : <WhatsApp color="disabled" style={{ fontSize: 48 }} />}
              <Typography variant="body2" color="textSecondary">{qrLoading ? "Consultando conexión…" : connected ? "Número sincronizado" : "Generá un QR para vincularlo"}</Typography>
            </div>}
            <div className={classes.qrInfo}>
              {connected ? <>
                <Typography variant="h6">Canal sincronizado</Typography>
                <Typography color="textSecondary" style={{ marginTop: 8 }}>Los mensajes entrantes llegan a Chatwoot y aparecen en Conversaciones del CRM.</Typography>
              </> : <>
                <Typography variant="h6">Escaneá el código</Typography>
                <Typography color="textSecondary" style={{ marginTop: 8 }}>En el teléfono: WhatsApp → Dispositivos vinculados → Vincular dispositivo.</Typography>
                {qr && qrExpiresAt > 0 && <Typography variant="body2" color={secondsLeft > 0 ? "textSecondary" : "primary"} style={{ marginTop: 10 }}>{secondsLeft > 0 ? `Se renueva automáticamente en ${secondsLeft} s.` : "Renovando el QR…"}</Typography>}
              </>}
              {qrError && <Typography variant="body2" className={classes.errorBox}>{qrError}</Typography>}
              <div className={classes.actions}>
                <Button variant="contained" color="primary" startIcon={qrLoading ? <CircularProgress size={18} /> : <Refresh />} disabled={qrLoading || connected} onClick={startWhatsApp}>{qr ? "Renovar QR" : "Generar QR"}</Button>
                <Button variant="outlined" startIcon={<WhatsApp />} disabled={qrLoading} onClick={() => loadQrForInstance(activeInstance, true)}>Comprobar conexión</Button>
                {connected && <Button variant="outlined" color="primary" startIcon={<Refresh />} disabled={qrLoading} onClick={relinkWithHistory}>Importar historial</Button>}
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
          <Button
            startIcon={deletingConnection ? <CircularProgress size={18} /> : <DeleteOutline />}
            disabled={deletingConnection || qrLoading}
            style={{ color: "#d32f2f" }}
            onClick={deleteWhatsappConnection}
          >
            {deletingConnection ? "Eliminando…" : "Eliminar canal"}
          </Button>
          <Button onClick={() => setWhatsappDialogOpen(false)} disabled={deletingConnection}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renameDialogOpen} onClose={() => !renamingConnection && setRenameDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Modificar nombre del canal</DialogTitle>
        <DialogContent>
          <Typography color="textSecondary" paragraph>Este será el nombre visible en el CRM y en Chatwoot. La conexión y el QR no cambiarán.</Typography>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            label="Nombre del canal"
            value={renameName}
            disabled={renamingConnection}
            onChange={event => setRenameName(event.target.value)}
            onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); renameWhatsappConnection(); } }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={renamingConnection} onClick={() => setRenameDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" startIcon={renamingConnection ? <CircularProgress size={18} /> : <Save />} disabled={renamingConnection || renameName.trim().length < 2} onClick={renameWhatsappConnection}>{renamingConnection ? "Guardando…" : "Guardar nombre"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={channelDialogOpen} onClose={() => setChannelDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle disableTypography>
          <div className={classes.dialogTitle}><Typography variant="h6">Configurar canal</Typography><IconButton aria-label="cerrar" onClick={() => setChannelDialogOpen(false)}><Close /></IconButton></div>
        </DialogTitle>
        <DialogContent dividers>
          {selectedChannel && <>
            <div className={classes.channelIdentity}>
              <div className={classes.channelIcon}>{iconFor(selectedChannel)}</div>
              <div><Typography variant="h6">{selectedChannel.name}</Typography><Typography color="textSecondary">{selectedChannel.type}</Typography></div>
              <Chip size="small" color="primary" label="Sincronizado" style={{ marginLeft: "auto" }} />
            </div>
            <div className={classes.detailGrid}>
              <Typography color="textSecondary">Equipo</Typography><Typography>{selectedChannel.team}</Typography>
              <Typography color="textSecondary">ID de bandeja</Typography><Typography>{selectedChannel.id}</Typography>
              <Typography color="textSecondary">Proveedor</Typography><Typography>Chatwoot</Typography>
              <Typography color="textSecondary">Estado</Typography><Typography>Recibiendo conversaciones</Typography>
            </div>
            <Divider style={{ margin: "22px 0" }} />
            <Typography variant="body2" color="textSecondary">La autenticación y permisos de Facebook/Instagram se administran desde Meta. El CRM consulta Chatwoot para mostrar el estado real.</Typography>
          </>}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Refresh />} onClick={() => refreshAll(true)}>Actualizar estado</Button>
          {selectedChannel?.raw?.type === "facebook" && <Button color="primary" onClick={() => { setChannelDialogOpen(false); setMetaDialogOpen(true); }}>Configurar Meta</Button>}
          <Button variant="contained" color="primary" onClick={() => window.open(`${chatwootBaseUrl}/settings/inboxes/${selectedChannel?.id || ""}`, "_blank", "noopener,noreferrer")}>Abrir en Chatwoot</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tiktokDialogOpen} onClose={() => setTiktokDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle disableTypography>
          <div className={classes.dialogTitle}>
            <div className={classes.channelIdentity}><div className={classes.channelIcon}><TikTokMark /></div><div><Typography variant="h6">TikTok Business</Typography><Typography variant="body2" color="textSecondary">Conexión mediante bandeja API</Typography></div></div>
            <IconButton aria-label="cerrar" onClick={() => setTiktokDialogOpen(false)}><Close /></IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>Para conectar TikTok necesitás una aplicación de desarrollador y una bandeja API que reciba sus eventos. El chat de ejemplo ya está disponible en Conversaciones para que veas el resultado final.</Typography>
          <div className={classes.detailGrid}>
            <Typography color="textSecondary">1. Aplicación</Typography><Typography>Crear u obtener las credenciales de TikTok Business.</Typography>
            <Typography color="textSecondary">2. Bandeja</Typography><Typography>Crear una bandeja API en Chatwoot.</Typography>
            <Typography color="textSecondary">3. Eventos</Typography><Typography>Configurar el webhook de la aplicación para enviar los mensajes a la bandeja.</Typography>
          </div>
        </DialogContent>
        <DialogActions style={{ flexWrap: "wrap" }}>
          <Button onClick={() => window.open("https://developers.tiktok.com/apps/", "_blank", "noopener,noreferrer")}>Abrir TikTok Developers</Button>
          <Button variant="contained" color="primary" onClick={() => window.open(chatwootNewInboxUrl, "_blank", "noopener,noreferrer")}>Crear bandeja API</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={metaDialogOpen} onClose={() => setMetaDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle disableTypography>
          <div className={classes.dialogTitle}><div><Typography variant="h6">Facebook e Instagram</Typography><Typography variant="body2" color="textSecondary">Configuración oficial de Meta</Typography></div><IconButton aria-label="cerrar" onClick={() => setMetaDialogOpen(false)}><Close /></IconButton></div>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}><Card variant="outlined"><CardContent><Facebook color="primary" fontSize="large" /><Typography variant="h6">Messenger</Typography><FormControlLabel control={<Checkbox checked={config.facebookMessenger} color="primary" onChange={event => updateConfig("facebookMessenger", event.target.checked)} />} label="Incluir permiso" /></CardContent></Card></Grid>
            <Grid item xs={12} md={4}><Card variant="outlined"><CardContent><Instagram style={{ color: "#e94b6a" }} fontSize="large" /><Typography variant="h6">Instagram Direct</Typography><FormControlLabel control={<Checkbox checked={config.instagramDirect} color="primary" onChange={event => updateConfig("instagramDirect", event.target.checked)} />} label="Incluir permiso" /></CardContent></Card></Grid>
            <Grid item xs={12} md={4}><Card variant="outlined"><CardContent><Security color="action" fontSize="large" /><Typography variant="h6">Leads de Facebook</Typography><FormControlLabel control={<Checkbox checked={config.facebookLeads} color="primary" onChange={event => updateConfig("facebookLeads", event.target.checked)} />} label="Incluir permiso" /></CardContent></Card></Grid>
            <Grid item xs={12} md={6}><TextField variant="outlined" label="ID de la app de Meta" fullWidth value={config.appId} onChange={event => updateConfig("appId", event.target.value)} helperText="Se obtiene en Meta for Developers." /></Grid>
            <Grid item xs={12} md={6}><TextField variant="outlined" label="Versión de Graph API" fullWidth value={config.apiVersion} onChange={event => updateConfig("apiVersion", event.target.value)} /></Grid>
            <Grid item xs={12}><TextField variant="outlined" label="URL de retorno OAuth" fullWidth value={config.redirectUri} onChange={event => updateConfig("redirectUri", event.target.value)} helperText="Debe coincidir exactamente con la URL configurada en Meta." /></Grid>
          </Grid>
          <Box mt={2}><Typography variant="body2" color="textSecondary">El secreto y los tokens permanecen únicamente en el servidor.</Typography></Box>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Save />} disabled={saving} onClick={saveMeta}>Guardar</Button>
          <Button startIcon={<Facebook />} variant="contained" color="primary" disabled={!config.appId || saving} onClick={connectMeta}>Conectar con Meta</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default MetaIntegration;
